import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, Pause, Volume2, Star, Users, Timer } from "lucide-react";
import { CHECKOUT_URL, IMAGES } from "@/lib/site";

const fade = (delay) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: "easeOut" },
});

export const Hero = () => {
  const videoRef = useRef(null);
  const [sound, setSound] = useState(false);
  const [paused, setPaused] = useState(false);

  const toggleVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!sound) {
      v.muted = false;
      v.play();
      setSound(true);
      setPaused(false);
    } else if (!paused) {
      v.pause();
      setPaused(true);
    } else {
      v.play();
      setPaused(false);
    }
  };

  useEffect(() => {
    const onFs = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.fullscreenElement) {
        v.controls = true;
        v.muted = false;
        v.play();
        setSound(true);
        setPaused(false);
      } else {
        v.controls = false;
      }
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const openFullscreen = () => videoRef.current?.requestFullscreen?.();

  return (
  <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-slate-950 pt-28 md:pt-40 pb-20 md:pb-28">
    <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl" aria-hidden="true" />
    <div className="absolute top-1/2 -left-40 w-[420px] h-[420px] rounded-full bg-orange-600/10 blur-3xl" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col lg:flex-row items-center gap-14 lg:gap-20">
      <div className="flex-1 max-w-2xl">
        <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full bg-slate-900 border border-slate-800 px-4 py-2 text-sm font-semibold text-slate-300 shadow-sm mb-7">
          <span className="flex text-orange-500">{[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</span>
          4.9 from 32,000+ app reviews
        </motion.div>
        <motion.h1 {...fade(0.1)} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.05]">
          Lose fat at home. <span className="text-blue-400">No gym.</span> No guesswork. <span className="text-orange-500">No excuses.</span>
        </motion.h1>
        <motion.p {...fade(0.2)} className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
          Simple 20-minute follow-along workouts built for busy, unmotivated people. Join 10,000+ members who finally got consistent — and got results — from their living room.
        </motion.p>
        <motion.div {...fade(0.3)} className="mt-9 flex flex-col sm:flex-row gap-4">
          <a data-testid="hero-cta-button" href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 text-white px-8 py-4 text-lg font-bold transition-[transform,box-shadow] duration-300 shadow-[0_8px_20px_0_rgb(37,99,235,0.3)] hover:shadow-[0_12px_25px_0_rgb(37,99,235,0.4)] hover:-translate-y-1 active:translate-y-0 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none focus-visible:ring-offset-2">
            Start Your 7-Day Free Trial <ArrowRight size={20} />
          </a>
          <a data-testid="hero-secondary-button" href="#workouts"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent text-white border-2 border-slate-700 px-8 py-4 text-lg font-bold transition-colors hover:border-blue-400 hover:bg-slate-900 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none focus-visible:ring-offset-2">
            <Play size={18} /> See the workouts
          </a>
        </motion.div>
        <motion.div {...fade(0.4)} className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400 font-medium">
          <span data-testid="hero-stat-members" className="flex items-center gap-2"><Users size={16} className="text-blue-400" /> 10,000+ transformations</span>
          <span className="flex items-center gap-2"><Timer size={16} className="text-blue-400" /> 15-25 min workouts</span>
          <span className="flex items-center gap-2"><Star size={16} className="text-blue-400" /> No equipment needed</span>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative flex-1 w-full max-w-lg lg:max-w-none">
        <video ref={videoRef} data-testid="hero-video" src={IMAGES.hero} poster={IMAGES.heroPoster} autoPlay muted loop playsInline preload="metadata" onClick={openFullscreen} aria-label="Bryan Lee presenting the 14-day transformation program — tap to watch fullscreen" className="rounded-[2.5rem] w-full h-[540px] sm:h-[480px] lg:h-[540px] object-cover cursor-pointer shadow-[0_30px_60px_rgb(0,0,0,0.12)]" />
        <button type="button" data-testid="hero-video-sound-button" onClick={toggleVideo}
          aria-label={!sound ? "Play video with sound" : paused ? "Play video" : "Pause video"}
          className="absolute bottom-5 right-5 z-20 inline-flex items-center gap-2 rounded-full bg-slate-900/80 backdrop-blur text-white text-sm font-bold px-4 py-2.5 hover:bg-blue-600 transition-colors focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none focus-visible:ring-offset-2">
          {!sound ? <><Volume2 size={16} /> Play with sound</> : paused ? <><Play size={16} /> Play</> : <><Pause size={16} /> Pause</>}
        </button>
        <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-slate-900 rounded-2xl shadow-[0_20px_40px_rgb(0,0,0,0.4)] border border-slate-800 px-6 py-4 z-10">
          <p className="font-display text-2xl font-extrabold text-white">10,247</p>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">lives transformed</p>
        </div>
        <div className="absolute top-4 left-3 sm:left-auto sm:top-6 sm:-right-6 bg-slate-900 border border-slate-700 text-white rounded-2xl shadow-xl px-3.5 py-2.5 sm:px-5 sm:py-3.5 z-10 flex items-center gap-2.5 sm:gap-3">
          <Play size={16} className="text-blue-400 shrink-0" />
          <div><p className="text-xs sm:text-sm font-bold leading-tight">Just press play</p><p className="text-[10px] sm:text-xs text-slate-400">follow along at home</p></div>
        </div>
      </motion.div>
    </div>
  </section>
  );
};
